const EvTires = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/ev-tires.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            Are EV Tires Different? What You Need to Know
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Electric vehicles demand more from their tires. Discover how EV tires differ — and why using the right ones matters for safety, efficiency, and performance.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            As electric vehicles (EVs) continue to grow in popularity, many drivers don’t realize that EVs require a different type of tire compared to traditional gasoline-powered cars. 
            EVs produce instant torque, carry heavier battery weight, and run more quietly — all of which demand specialized tire technology.
            At <strong>Express Tire Works</strong>, we help EV owners choose the right tires designed to maximize range, safety, and performance.
            Here’s everything you need to know.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. Why EV Tires Are Different
            </h2>
            <p>
              Electric vehicles are engineered differently, and their tires must support these unique characteristics. The most important differences include:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Heavier vehicle weight:</strong> Batteries add hundreds of pounds, increasing pressure on tires.</li>
              <li><strong>Instant torque:</strong> EVs deliver immediate acceleration, generating more tire wear.</li>
              <li><strong>Quiet motor:</strong> Less engine noise means tire noise becomes more noticeable.</li>
              <li><strong>Range sensitivity:</strong> Tire rolling resistance directly affects EV driving range.</li>
            </ul>
            <p>
              Because of these factors, most manufacturers design tires specifically for EV performance and longevity.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Key Features of EV-Specific Tires
            </h2>
            <p>
              EV tires include specialized technologies and construction that set them apart from standard tires. Here are the features that matter:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Low Rolling Resistance:</strong> Helps maximize battery efficiency and extend driving range.</li>
              <li><strong>Reinforced Sidewalls:</strong> Supports the extra weight of EV batteries.</li>
              <li><strong>Noise-Reduction Foam:</strong> Many EV tires include acoustic foam to minimize road noise.</li>
              <li><strong>Durable Rubber Compounds:</strong> Designed to withstand higher torque output.</li>
              <li><strong>Aerodynamic Tread Patterns:</strong> Reduces drag and improves efficiency.</li>
            </ul>
            <p>
              These engineering improvements make EV tires noticeably more efficient and durable under electric-vehicle conditions.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Can You Use Regular Tires on an EV?
            </h2>
            <p>
              Technically, yes — you can install regular tires on an electric vehicle. 
              But doing so comes with major drawbacks:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Reduced driving range</li>
              <li>Higher energy consumption</li>
              <li>Increased road noise</li>
              <li>Faster and uneven tire wear</li>
              <li>Poorer braking and cornering stability</li>
            </ul>
            <p>
              Standard tires aren’t built to handle EV torque and weight, meaning they wear out much faster and compromise performance.
              For this reason, most experts strongly recommend EV-specific tires.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. How Long Do EV Tires Last?
            </h2>
            <p>
              EV tires typically wear faster than regular tires because of:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Higher torque output</li>
              <li>Greater vehicle weight</li>
              <li>Stronger acceleration patterns</li>
            </ul>
            <p>
              On average, EV tires last around <strong>25,000–40,000 miles</strong>, depending on driving habits.
              Regular tire rotations every <strong>6,000–8,000 miles</strong> can significantly help extend their lifespan.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. Best Brands That Make EV Tires
            </h2>
            <p>
              Many leading tire manufacturers now produce EV-focused models. Popular brands include:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Michelin EV Series</li>
              <li>Bridgestone Turanza EV</li>
              <li>Goodyear ElectricDrive</li>
              <li>Continental EcoContact Hybrid</li>
              <li>Pirelli Elect Series</li>
            </ul>
            <p>
              At <strong>Express Tire Works</strong>, we offer a wide variety of EV tires tailored for efficiency, comfort, and long-lasting performance.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. Do EV Tires Cost More?
            </h2>
            <p>
              Yes — EV tires are usually <strong>10–20% more expensive</strong> than traditional tires.  
              This is because they require:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Advanced rubber compounds</li>
              <li>Reinforced construction</li>
              <li>Noise-canceling technologies</li>
              <li>Precision engineering for range efficiency</li>
            </ul>
            <p>
              While the initial cost is higher, EV tires often save drivers money long-term through improved range and durability.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              EV tires play a critical role in handling, safety, efficiency, and comfort. 
              While they may cost slightly more, they offer major benefits like reduced noise, better traction, increased range, and longer tread life.  
              If you own an electric vehicle, investing in EV-specific tires ensures the best possible driving experience.
            </p>
            <p className="mt-4">
              Visit <strong>Express Tire Works</strong> to explore top EV tire brands and get professional recommendations tailored to your vehicle and driving style.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">EV Tires</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Electric Vehicles</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Technology</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Maintenance</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Range Efficiency</p>
        </div>
      </section>
    </div>
  );
};

export default EvTires;